import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"

import type { AugmentedFeedback } from "@productlane/api/src/lib/timeline-events"
import type { ThreadTimelineCursor } from "@productlane/zero/queries"
import type { CustomView } from "@productlane/zero/schema"
import { hasEnabledEmail } from "@productlane/lib/src/email-settings"
import {
	feedbackDetailMessageLimitMax,
	feedbackDetailMessagePageSize,
	queries,
	threadDetailQueryArgs,
} from "@productlane/zero/queries"

import type {
	ExtractedLink,
	ThreadContextState,
	ThreadTab,
} from "../thread-context"
import type {
	InboxState,
	ThreadAnchor,
	ThreadAnchorCursor,
} from "@/lib/states/global-state"
import type { AssignedSubTab } from "@/screens/app/inbox/helpers/assigned-sub-tab"
import { useThreadState } from "@/lib/states/global-state"
import { useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	resolveCustomViewStateScope,
	resolveEffectiveInboxTab,
	viewOwnsStatusFilter,
} from "@/screens/app/inbox/helpers/assigned-sub-tab"
import { getListNeighborId } from "./get-list-neighbor-id"
import { pinThreadCursor } from "./thread-nav-cursor"
import { useThreadsForList } from "./use-threads-for-list"

// Preload full details for a thread id so `useQuery(threadWithFullDetails)`
// returns synced data instantly when the user navigates to it. Used for
// next/prev warming so arrow-key nav feels instant.
const usePreloadThread = (
	zero: ReturnType<typeof useZero>,
	decodedToken: ReturnType<typeof useAuthData>,
	threadId: string | undefined,
) => {
	useEffect(() => {
		if (!threadId) return
		const { cleanup } = zero.preload(
			queries.threadWithFullDetails(threadDetailQueryArgs(threadId)),
			{
				ttl: "10m",
			},
		)
		return cleanup
	}, [zero, decodedToken, threadId])
}

// Step for healing the base window when new messages slide rows out of it.
const BASE_LIMIT_GROW_STEP = 100

interface TimelinePaginationState {
	threadId: string | undefined
	baseLimit: number
	pages: ThreadTimelineCursor[]
}

const initialPaginationState = (
	threadId: string | undefined,
): TimelinePaginationState => ({
	threadId,
	baseLimit: feedbackDetailMessagePageSize,
	pages: [],
})

// "Show older messages" appends a fixed cursor page; the base window only
// grows to re-overlap page 1 after new messages slide rows out of it.
const useThreadTimelinePagination = (threadId: string | undefined) => {
	const [state, setState] = useState(() => initialPaginationState(threadId))
	// Reset during render so a thread switch never fires a query with the
	// previous thread's raised limit or cursor pages.
	if (state.threadId !== threadId) {
		setState(initialPaginationState(threadId))
	}
	const current =
		state.threadId === threadId ? state : initialPaginationState(threadId)

	const loadOlderMessages = useCallback((cursor: ThreadTimelineCursor) => {
		setState((s) => {
			if (
				s.pages.some(
					(p) => p.id === cursor.id && p.createdAt === cursor.createdAt,
				)
			) {
				return s
			}
			return { ...s, pages: [...s.pages, cursor] }
		})
	}, [])

	const growBaseLimit = useCallback(() => {
		setState((s) => {
			const next = Math.min(
				s.baseLimit + BASE_LIMIT_GROW_STEP,
				feedbackDetailMessageLimitMax,
			)
			return next === s.baseLimit ? s : { ...s, baseLimit: next }
		})
	}, [])

	return {
		baseLimit: current.baseLimit,
		pages: current.pages,
		loadOlderMessages,
		growBaseLimit,
	}
}

const useOpenThreadCursor = (
	thread: AugmentedFeedback | undefined,
): ThreadAnchorCursor | undefined => {
	const pinned = useRef<ThreadAnchorCursor | undefined>(undefined)
	pinned.current = pinThreadCursor(pinned.current, thread)
	return thread ? pinned.current : undefined
}

// Resolves the same effective tab, stateScope, and custom-view filters as the
// displayed list, so next/prev (j/k, e, h) walk the list the user sees.
const useActiveViewContext = (): {
	tab: InboxState
	overrideFilters?: CustomView["filters"]
	stateScope?: AssignedSubTab
} => {
	const inboxState = useThreadState((s) => s.inboxState)
	const activeCustomViewId = useThreadState((s) => s.activeCustomViewId)
	const assignedSubTab = useThreadState((s) => s.assignedSubTab)
	const [customViews] = useQuery(queries.customViews(), {
		ttl: "10m",
	})
	const overrideFilters = useMemo(() => {
		if (inboxState !== "custom-view" || !activeCustomViewId) return undefined
		return customViews.find((v) => v.id === activeCustomViewId)?.filters
	}, [inboxState, activeCustomViewId, customViews])
	const tab = resolveEffectiveInboxTab(inboxState, assignedSubTab)
	const stateScope = resolveCustomViewStateScope(
		inboxState,
		!!activeCustomViewId,
		assignedSubTab,
		viewOwnsStatusFilter(overrideFilters),
	)
	return { tab, overrideFilters, stateScope }
}

const useSearchListNeighbor = (
	thread: AugmentedFeedback | undefined,
	direction: "forward" | "backward",
): { id: string } | undefined | null => {
	const inboxSearchThreadIds = useThreadState((s) => s.inboxSearchThreadIds)
	return useMemo(() => {
		if (inboxSearchThreadIds.length === 0) return null
		if (!thread) return undefined
		const neighborId = getListNeighborId(
			inboxSearchThreadIds,
			thread.id,
			direction,
		)
		return neighborId ? { id: neighborId } : undefined
	}, [thread, inboxSearchThreadIds, direction])
}

// The inbox publishes the open thread's neighbours in rendered list order.
// They win over the cursor query, which can only re-derive the order from the
// sort args and drifts from the list the reader is looking at.
const useInboxListNeighbor = (
	thread: AugmentedFeedback | undefined,
	direction: "forward" | "backward",
): { id: string } | undefined => {
	const neighbors = useThreadState((s) => s.inboxNavNeighbors)
	return useMemo(() => {
		if (!thread || neighbors?.threadId !== thread.id) return undefined
		const id = direction === "forward" ? neighbors.nextId : neighbors.prevId
		return id ? { id } : undefined
	}, [thread, neighbors, direction])
}

const useNextThread = (thread?: AugmentedFeedback) => {
	const { tab, overrideFilters, stateScope } = useActiveViewContext()
	const searchNeighbor = useSearchListNeighbor(thread, "forward")
	const listNeighbor = useInboxListNeighbor(thread, "forward")
	const cursor = useOpenThreadCursor(thread)
	const anchor = useMemo<ThreadAnchor>(() => {
		return {
			direction: "forward",
			cursor,
			index: 0,
		}
	}, [cursor])

	const { threads, isLoading } = useThreadsForList({
		tab,
		pageSize: 1,
		anchor,
		overrideFilters,
		stateScope,
	})

	const nextThread = useMemo(() => {
		if (searchNeighbor !== null) return searchNeighbor
		if (listNeighbor) return listNeighbor
		return threads.length > 0 ? threads[0] : undefined
	}, [searchNeighbor, listNeighbor, threads])
	return { nextThread, isLoading }
}

const usePrevThread = (thread?: AugmentedFeedback) => {
	const { tab, overrideFilters, stateScope } = useActiveViewContext()
	const searchNeighbor = useSearchListNeighbor(thread, "backward")
	const listNeighbor = useInboxListNeighbor(thread, "backward")
	const cursor = useOpenThreadCursor(thread)
	const anchor = useMemo<ThreadAnchor>(() => {
		return {
			direction: "backward",
			cursor,
			index: 1,
		}
	}, [cursor])

	const { threads, isLoading } = useThreadsForList({
		tab,
		pageSize: 1,
		anchor,
		overrideFilters,
		stateScope,
	})

	const prevThread = useMemo(() => {
		if (searchNeighbor !== null) return searchNeighbor
		if (listNeighbor) return listNeighbor
		return threads.length > 0 ? threads[0] : undefined
	}, [searchNeighbor, listNeighbor, threads])
	return { prevThread, isLoading }
}

export const useInitThread = (): ThreadContextState => {
	const params = useParams({ strict: false })
	const id = params.id
	const tab = params.tab as ThreadTab
	const decodedToken = useAuthData()
	const navigate = useNavigate()
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const { baseLimit, pages, loadOlderMessages, growBaseLimit } =
		useThreadTimelinePagination(id)
	const [thread, queryResult] = useQuery(
		queries.threadWithFullDetails(threadDetailQueryArgs(id ?? "", baseLimit)),
		{
			ttl: "10m",
			enabled: !!id,
		},
	)

	const { nextThread } = useNextThread(thread)
	const { prevThread } = usePrevThread(thread)

	// Preload full details for next/prev so arrow-key nav is instant.
	// Zero dedupes by query hash, so calling preload here on every render of
	// useInitThread is cheap. The cleanup callback releases the subscription
	// when we move on; the `ttl: "10m"` keeps data warm for back-nav.
	usePreloadThread(zero, decodedToken, nextThread?.id)
	usePreloadThread(zero, decodedToken, prevThread?.id)

	const goNext = useCallback(() => {
		if (!nextThread || !thread) {
			return
		}
		const currentPath = window.location.pathname
		const newPath = currentPath.replace(thread.id, nextThread.id)

		void navigate({ href: newPath, replace: true })
	}, [nextThread, thread, navigate])

	const goPrev = useCallback(() => {
		if (!prevThread || !thread) {
			return
		}
		const currentPath = window.location.pathname
		const newPath = currentPath.replace(thread.id, prevThread.id)

		void navigate({ href: newPath, replace: true })
	}, [prevThread, thread, navigate])

	const [isReply, _setIsReply] = useState(false)
	const [isComment, _setIsComment] = useState(false)
	const setIsReply = useCallback(
		(isReply: boolean) => {
			// Allow reply for email, Slack, or live chat
			const hasEmailEnabled = hasEnabledEmail(workspace?.emailSettings)
			const hasSlackEnabled = !!workspace?.slackConnectSettings
			const hasLiveChat =
				thread?.liveChatSession && !thread.liveChatSession.isFinished

			if (!hasEmailEnabled && !hasSlackEnabled && !hasLiveChat) {
				return
			}
			_setIsReply(isReply)
			if (isReply) {
				_setIsComment(false)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			// eslint-disable-next-line react-hooks/exhaustive-deps
			hasEnabledEmail(workspace?.emailSettings),
			workspace?.slackConnectSettings,
			thread?.liveChatSession,
		],
	)
	const setIsComment = useCallback((isComment: boolean) => {
		_setIsComment(isComment)
		if (isComment) {
			_setIsReply(false)
		}
	}, [])

	// Editor links state
	const [extractedLinks, setExtractedLinks] = useState<ExtractedLink[]>([])

	// Reset per-thread state on navigation. The surrounding components no
	// longer remount on thread change, so we clear isReply/isComment/links
	// explicitly when the url-derived id changes.
	useEffect(() => {
		_setIsReply(false)
		_setIsComment(false)
		setExtractedLinks([])
	}, [id])

	return {
		thread: thread as AugmentedFeedback,
		goNext,
		goPrev,
		nextThread: nextThread ?? null,
		prevThread: prevThread ?? null,
		tab,
		changeTab: () => {
			throw new Error("Not implemented")
		},
		isReply,
		setIsReply,
		isComment,
		setIsComment,
		queryResult,
		baseLimit,
		pages,
		loadOlderMessages,
		growBaseLimit,
		extractedLinks,
		setExtractedLinks,
	}
}

type ThreadDetailCore = Omit<
	ThreadContextState,
	"goNext" | "goPrev" | "nextThread" | "prevThread"
>

/** A thread's detail state without list navigation; callers bring their own next/prev. */
export const useThreadDetailCore = (threadId: string): ThreadDetailCore => {
	const workspace = useZeroWorkspace()
	const { baseLimit, pages, loadOlderMessages, growBaseLimit } =
		useThreadTimelinePagination(threadId)
	const [thread, queryResult] = useQuery(
		queries.threadWithFullDetails(threadDetailQueryArgs(threadId, baseLimit)),
		{
			ttl: "10m",
			enabled: !!threadId,
		},
	)

	const [isReply, _setIsReply] = useState(false)
	const [isComment, _setIsComment] = useState(false)
	const setIsReply = useCallback(
		(isReply: boolean) => {
			const hasEmailEnabled = hasEnabledEmail(workspace?.emailSettings)
			const hasSlackEnabled = !!workspace?.slackConnectSettings
			const hasLiveChat =
				thread?.liveChatSession && !thread.liveChatSession.isFinished

			if (!hasEmailEnabled && !hasSlackEnabled && !hasLiveChat) {
				return
			}
			_setIsReply(isReply)
			if (isReply) {
				_setIsComment(false)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			// eslint-disable-next-line react-hooks/exhaustive-deps
			hasEnabledEmail(workspace?.emailSettings),
			workspace?.slackConnectSettings,
			thread?.liveChatSession,
		],
	)
	const setIsComment = useCallback((isComment: boolean) => {
		_setIsComment(isComment)
		if (isComment) {
			_setIsReply(false)
		}
	}, [])

	const [extractedLinks, setExtractedLinks] = useState<ExtractedLink[]>([])

	// Reset per-thread state when navigating to a new thread. The hook no
	// longer remounts on thread change (parent dropped its key), so the state
	// would otherwise leak from the previous thread.
	useEffect(() => {
		_setIsReply(false)
		_setIsComment(false)
		setExtractedLinks([])
	}, [threadId])

	// Every timeline message reads this through ThreadContext, so a new object
	// re-renders the whole conversation.
	return useMemo(
		() => ({
			thread: thread as AugmentedFeedback,
			tab: "timeline" as const,
			changeTab: ignoreTabChange,
			isReply,
			setIsReply,
			isComment,
			setIsComment,
			queryResult,
			baseLimit,
			pages,
			loadOlderMessages,
			growBaseLimit,
			extractedLinks,
			setExtractedLinks,
		}),
		[
			thread,
			isReply,
			setIsReply,
			isComment,
			setIsComment,
			queryResult,
			baseLimit,
			pages,
			loadOlderMessages,
			growBaseLimit,
			extractedLinks,
		],
	)
}

// Tab changes are not supported in the embedded detail view.
const ignoreTabChange = () => undefined

export const useInitThreadForId = (threadId: string): ThreadContextState => {
	const navigate = useNavigate()
	const zero = useZero()
	const decodedToken = useAuthData()
	const core = useThreadDetailCore(threadId)
	const { thread } = core

	const { nextThread } = useNextThread(thread)
	const { prevThread } = usePrevThread(thread)
	usePreloadThread(zero, decodedToken, nextThread?.id)
	usePreloadThread(zero, decodedToken, prevThread?.id)

	const goNext = useCallback(() => {
		if (!nextThread || !thread) {
			return
		}
		const currentPath = window.location.pathname
		const newPath = currentPath.replace(thread.id, nextThread.id)

		void navigate({ href: newPath, replace: true })
	}, [nextThread, thread, navigate])

	const goPrev = useCallback(() => {
		if (!prevThread || !thread) {
			return
		}
		const currentPath = window.location.pathname
		const newPath = currentPath.replace(thread.id, prevThread.id)

		void navigate({ href: newPath, replace: true })
	}, [prevThread, thread, navigate])

	return useMemo(
		() => ({
			...core,
			goNext,
			goPrev,
			nextThread: nextThread ?? null,
			prevThread: prevThread ?? null,
		}),
		[core, goNext, goPrev, nextThread, prevThread],
	)
}
